// frontend/src/services/translationService.js
/**
 * Translation Service - Frontend service for managing content translations
 * 
 * Provides methods to:
 * - Get/set entity default language
 * - Manage translations for content
 * - Get translated content with fallback logic
 * - Cache translated content for performance
 */

import axios from '../axiosConfig';

class TranslationService {
    constructor() {
        this.cache = new Map();
        this.routeWaypointCache = new Map();
        this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
    }

    isCacheEntryValid(cacheEntry, originalContent) {
        return cacheEntry
            && Date.now() - cacheEntry.timestamp < this.cacheTimeout
            && cacheEntry.originalContent === originalContent;
    }

    cloneRoutes(routes) {
        if (!Array.isArray(routes)) return routes;
        return routes.map(route => ({
            ...route,
            _translationMeta: route?._translationMeta ? { ...route._translationMeta } : route?._translationMeta,
            waypoints: route.waypoints?.map(waypoint => ({
                ...waypoint,
                _translationMeta: waypoint?._translationMeta ? { ...waypoint._translationMeta } : waypoint?._translationMeta
            }))
        }));
    }

    /**
     * Get all translations for a content item
     * @param {number} entityId - Entity ID
     * @param {string} contentType - Content type
     * @param {number} contentId - Content ID
     * @returns {Promise<Object>} Translations object
     */
    async getTranslations(entityId, contentType, contentId) {
        try {
            const response = await axios.get(`/api/translations/entity/${entityId}/${contentType}/${contentId}`);
            return response.data.data;
        } catch (error) {
            console.error('Error getting translations:', error);
            return { translations: {}, defaultLanguage: 'en' };
        }
    }

    /**
     * Save translations for a content item
     * @param {number} entityId - Entity ID
     * @param {string} contentType - Content type
     * @param {number} contentId - Content ID
     * @param {Object} translations - Translations object
     * @returns {Promise<boolean>} Success status
     */
    async saveTranslations(entityId, contentType, contentId, translations) {
        try {
            await axios.put(`/api/translations/entity/${entityId}/${contentType}/${contentId}`, {
                translations
            });
            
            // Clear cache for this content
            this.clearContentCache(entityId, contentType, contentId);
            
            return true;
        } catch (error) {
            console.error('Error saving translations:', error);
            throw error;
        }
    }

    /**
     * Get translated content with fallback logic
     * @param {number} entityId - Entity ID
     * @param {string} contentType - Content type
     * @param {number} contentId - Content ID
     * @param {string} fieldName - Field name
     * @param {string} userLanguage - User's preferred language
     * @param {string} originalContent - Original content from main table
     * @returns {Promise<Object>} Content with metadata
     */
    async getTranslatedContent(entityId, contentType, contentId, fieldName, userLanguage, originalContent) {
        const cacheKey = `content_${entityId}_${contentType}_${contentId}_${fieldName}_${userLanguage}`;
        
        if (this.cache.has(cacheKey)) {
            const cached = this.cache.get(cacheKey);
            if (this.isCacheEntryValid(cached, originalContent)) {
                return cached.data;
            }
        }
        
        try {
            const response = await axios.get(
                `/api/translations/entity/${entityId}/${contentType}/${contentId}/${fieldName}/${userLanguage}`,
                { params: { originalContent } }
            );
            
            const result = response.data.data;
            
            // Cache the result
            this.cache.set(cacheKey, {
                data: result,
                timestamp: Date.now(),
                originalContent
            });
            
            return result;
        } catch (error) {
            console.error('Error getting translated content:', error);
            return {
                content: originalContent,
                language: userLanguage,
                source: 'fallback',
                isTranslated: false,
                fallbackReason: 'API error'
            };
        }
    }

    /**
     * Batch fetch translations for multiple content items
     * Optimized for performance when loading many items at once
     * @param {number} entityId - Entity ID
     * @param {string} contentType - Content type
     * @param {Array} items - Array of {id, fields: {fieldName: originalContent}}
     * @param {string} userLanguage - User's preferred language
     * @returns {Promise<Map>} Map of contentId -> fieldName -> translatedContent
     */
    async batchGetTranslations(entityId, contentType, items, userLanguage) {
        if (!items || items.length === 0) {
            return new Map();
        }

        // Create a map to store results
        const translationsMap = new Map();
        const itemsToFetch = [];
        const itemsById = new Map(items.map(item => [Number(item.id), item]));

        // Check cache first
        for (const item of items) {
            const itemTranslations = {};
            let needsFetch = false;

            for (const [fieldName, originalContent] of Object.entries(item.fields)) {
                const cacheKey = `content_${entityId}_${contentType}_${item.id}_${fieldName}_${userLanguage}`;
                
                if (this.cache.has(cacheKey)) {
                    const cached = this.cache.get(cacheKey);
                    if (this.isCacheEntryValid(cached, originalContent)) {
                        itemTranslations[fieldName] = cached.data;
                        continue;
                    }
                }
                
                needsFetch = true;
                itemTranslations[fieldName] = null; // Mark as needing fetch
            }

            if (needsFetch) {
                itemsToFetch.push({ ...item, cachedTranslations: itemTranslations });
            } else {
                translationsMap.set(item.id, itemTranslations);
            }
        }

        // If all items were cached, return early
        if (itemsToFetch.length === 0) {
            return translationsMap;
        }

        try {
            // Make batch API call for items that need fetching
            const response = await axios.post(
                `/api/translations/entity/${entityId}/${contentType}/batch`,
                {
                    items: itemsToFetch.map(item => ({
                        id: item.id,
                        fields: item.fields
                    })),
                    userLanguage
                }
            );

            const batchResults = response.data.data;

            // Process batch results and update cache
            for (const [contentId, translations] of Object.entries(batchResults)) {
                const id = parseInt(contentId);
                const originalItem = itemsById.get(id);
                const itemTranslations = {};
                
                for (const [fieldName, translationData] of Object.entries(translations)) {
                    itemTranslations[fieldName] = translationData;
                    
                    // Update cache
                    const cacheKey = `content_${entityId}_${contentType}_${id}_${fieldName}_${userLanguage}`;
                    this.cache.set(cacheKey, {
                        data: translationData,
                        timestamp: Date.now(),
                        originalContent: originalItem?.fields?.[fieldName]
                    });
                }
                
                translationsMap.set(id, itemTranslations);
            }

            // For items that failed, use fallback
            for (const item of itemsToFetch) {
                if (!translationsMap.has(item.id)) {
                    const fallbackTranslations = {};
                    for (const [fieldName, originalContent] of Object.entries(item.fields)) {
                        fallbackTranslations[fieldName] = {
                            content: originalContent,
                            language: userLanguage,
                            source: 'fallback',
                            isTranslated: false,
                            fallbackReason: 'Not in batch response'
                        };
                    }
                    translationsMap.set(item.id, fallbackTranslations);
                }
            }

        } catch (error) {
            console.error('Error batch fetching translations:', error);
            
            // Fallback for all items on error
            for (const item of itemsToFetch) {
                const fallbackTranslations = {};
                for (const [fieldName, originalContent] of Object.entries(item.fields)) {
                    fallbackTranslations[fieldName] = {
                        content: originalContent,
                        language: userLanguage,
                        source: 'fallback',
                        isTranslated: false,
                        fallbackReason: 'Batch API error'
                    };
                }
                translationsMap.set(item.id, fallbackTranslations);
            }
        }

        return translationsMap;
    }

    /**
     * Translate route collections and nested waypoint content in one batched pass.
     *
     * mode='replace' mutates display fields like name/address for rider-facing data.
     * mode='display' preserves original fields and adds displayName/displayAddress,
     * which is safer for admin edit screens that save the default-language content.
     */
    async translateRoutesWithWaypoints(routes, {
        entityId,
        userLanguage,
        defaultLanguage = 'en',
        routeFields = ['name', 'description'],
        waypointFields = ['name', 'address'],
        mode = 'replace'
    } = {}) {
        if (!Array.isArray(routes) || routes.length === 0 || !entityId) {
            return routes;
        }

        const displayFieldName = (fieldName) => (
            `display${fieldName.charAt(0).toUpperCase()}${fieldName.slice(1)}`
        );

        const cacheSignature = JSON.stringify({
            entityId,
            userLanguage,
            defaultLanguage,
            routeFields,
            waypointFields,
            mode,
            routes: routes.map(route => ({
                id: route.id,
                fields: routeFields.map(fieldName => route?.[fieldName] || ''),
                waypoints: route.waypoints?.map(waypoint => ({
                    id: waypoint.id,
                    fields: waypointFields.map(fieldName => waypoint?.[fieldName] || '')
                })) || []
            }))
        });
        const routeWaypointCacheKey = `routes_with_waypoints_${cacheSignature}`;
        const cachedRoutes = this.routeWaypointCache.get(routeWaypointCacheKey);
        if (cachedRoutes && Date.now() - cachedRoutes.timestamp < this.cacheTimeout) {
            return this.cloneRoutes(cachedRoutes.data);
        }

        const applyFields = (item, translations, fields) => {
            const nextItem = { ...item };
            const translationMeta = {};

            fields.forEach((fieldName) => {
                const originalContent = item?.[fieldName] || '';
                const translatedContent = translations?.[fieldName];
                const resolvedContent = translatedContent?.content || originalContent;

                if (mode === 'display') {
                    nextItem[displayFieldName(fieldName)] = resolvedContent;
                } else {
                    nextItem[fieldName] = resolvedContent;
                }

                translationMeta[fieldName] = translatedContent || {
                    content: originalContent,
                    language: userLanguage || defaultLanguage,
                    source: 'original',
                    isTranslated: false
                };
            });

            nextItem._translationMeta = {
                ...(item?._translationMeta || {}),
                ...translationMeta
            };

            return nextItem;
        };

        const shouldFetchTranslations = userLanguage && userLanguage !== defaultLanguage;

        try {
            let routeTranslations = new Map();
            let waypointTranslations = new Map();

            if (shouldFetchTranslations) {
                const routeItems = routes.map((route) => ({
                    id: route.id,
                    fields: routeFields.reduce((fields, fieldName) => ({
                        ...fields,
                        [fieldName]: route?.[fieldName] || ''
                    }), {})
                }));

                const waypointItemsById = new Map();
                routes.forEach((route) => {
                    route.waypoints?.forEach((waypoint) => {
                        if (!waypoint?.id || waypointItemsById.has(waypoint.id)) {
                            return;
                        }

                        waypointItemsById.set(waypoint.id, {
                            id: waypoint.id,
                            fields: waypointFields.reduce((fields, fieldName) => ({
                                ...fields,
                                [fieldName]: waypoint?.[fieldName] || ''
                            }), {})
                        });
                    });
                });

                const waypointItems = Array.from(waypointItemsById.values());
                [routeTranslations, waypointTranslations] = await Promise.all([
                    this.batchGetTranslations(entityId, 'route', routeItems, userLanguage),
                    waypointItems.length
                        ? this.batchGetTranslations(entityId, 'waypoint', waypointItems, userLanguage)
                        : Promise.resolve(new Map())
                ]);
            }

            const translatedRoutes = routes.map((route) => {
                const translatedWaypoints = route.waypoints?.map((waypoint) => (
                    applyFields(waypoint, waypointTranslations.get(waypoint.id), waypointFields)
                ));

                return {
                    ...applyFields(route, routeTranslations.get(route.id), routeFields),
                    waypoints: translatedWaypoints || route.waypoints
                };
            });
            this.routeWaypointCache.set(routeWaypointCacheKey, {
                data: this.cloneRoutes(translatedRoutes),
                timestamp: Date.now()
            });
            return this.cloneRoutes(translatedRoutes);
        } catch (error) {
            console.warn('Failed to translate routes and waypoints:', error);
            const fallbackRoutes = routes.map((route) => ({
                ...applyFields(route, null, routeFields),
                waypoints: route.waypoints?.map((waypoint) => applyFields(waypoint, null, waypointFields)) || route.waypoints
            }));
            this.routeWaypointCache.set(routeWaypointCacheKey, {
                data: this.cloneRoutes(fallbackRoutes),
                timestamp: Date.now()
            });
            return this.cloneRoutes(fallbackRoutes);
        }
    }

    /**
     * Get translation statistics for an entity
     * @param {number} entityId - Entity ID
     * @returns {Promise<Object>} Translation statistics
     */
    async getTranslationStats(entityId) {
        try {
            const response = await axios.get(`/api/translations/entity/${entityId}/stats`);
            return response.data.data;
        } catch (error) {
            console.error('Error getting translation stats:', error);
            return {
                defaultLanguage: 'en',
                availableLanguages: [],
                translationStats: []
            };
        }
    }

    /**
     * Delete a specific translation
     * @param {number} entityId - Entity ID
     * @param {string} contentType - Content type
     * @param {number} contentId - Content ID
     * @param {string} fieldName - Field name
     * @param {string} languageCode - Language code
     * @returns {Promise<boolean>} Success status
     */
    async deleteTranslation(entityId, contentType, contentId, fieldName, languageCode) {
        try {
            await axios.delete(`/api/translations/entity/${entityId}/${contentType}/${contentId}/${fieldName}/${languageCode}`);
            
            // Clear cache for this content
            this.clearContentCache(entityId, contentType, contentId);
            
            return true;
        } catch (error) {
            console.error('Error deleting translation:', error);
            throw error;
        }
    }

    /**
     * Delete all translations for a content item (when content is deleted)
     * @param {number} entityId - Entity ID
     * @param {string} contentType - Content type
     * @param {number} contentId - Content ID
     * @returns {Promise<number>} Number of translations deleted
     */
    async deleteAllTranslations(entityId, contentType, contentId) {
        try {
            const response = await axios.delete(`/api/translations/entity/${entityId}/${contentType}/${contentId}`);
            
            // Clear cache for this content
            this.clearContentCache(entityId, contentType, contentId);
            
            return response.data.data.deletedCount;
        } catch (error) {
            console.error('Error deleting all translations:', error);
            throw error;
        }
    }

    /**
     * Helper method to enhance content objects with translations
     * This is useful for lists of content (routes, entities, etc.)
     * 
     * @param {Array} contentItems - Array of content objects
     * @param {number} entityId - Entity ID
     * @param {string} contentType - Content type
     * @param {string} userLanguage - User's preferred language
     * @param {Array} fieldsToTranslate - Fields to translate (e.g., ['name', 'description'])
     * @param {string} defaultLanguage - Entity's default language (optional, defaults to 'en')
     * @returns {Promise<Array>} Enhanced content items with translations
     */
    async enhanceContentWithTranslations(contentItems, entityId, contentType, userLanguage, fieldsToTranslate = ['name', 'description'], defaultLanguage = 'en') {
        if (!contentItems || contentItems.length === 0) {
            return contentItems;
        }

        try {
            // If user language matches default, no translation needed
            if (userLanguage === defaultLanguage) {
                return contentItems.map(item => ({
                    ...item,
                    _translationMeta: { source: 'original', language: defaultLanguage }
                }));
            }

            // Get translations for all items
            const enhancedItems = await Promise.all(
                contentItems.map(async (item) => {
                    const translatedItem = { ...item };
                    const translationMeta = {
                        originalLanguage: defaultLanguage,
                        targetLanguage: userLanguage,
                        translatedFields: {}
                    };

                    // Get translations for each field
                    for (const fieldName of fieldsToTranslate) {
                        if (item[fieldName]) {
                            try {
                                const translatedContent = await this.getTranslatedContent(
                                    entityId,
                                    contentType,
                                    item.id,
                                    fieldName,
                                    userLanguage,
                                    item[fieldName]
                                );

                                translatedItem[fieldName] = translatedContent.content;
                                translationMeta.translatedFields[fieldName] = {
                                    source: translatedContent.source,
                                    isTranslated: translatedContent.isTranslated
                                };
                            } catch (error) {
                                console.warn(`Failed to translate ${fieldName} for ${contentType} ${item.id}:`, error);
                                // Keep original content on error
                                translationMeta.translatedFields[fieldName] = {
                                    source: 'error',
                                    isTranslated: false
                                };
                            }
                        }
                    }

                    translatedItem._translationMeta = translationMeta;
                    return translatedItem;
                })
            );

            return enhancedItems;
        } catch (error) {
            console.error('Error enhancing content with translations:', error);
            // Return original content on error
            return contentItems.map(item => ({
                ...item,
                _translationMeta: { source: 'error', language: userLanguage }
            }));
        }
    }

    /**
     * Clear cache for specific content
     * @param {number} entityId - Entity ID
     * @param {string} contentType - Content type
     * @param {number} contentId - Content ID
     */
    clearContentCache(entityId, contentType, contentId) {
        const keysToDelete = [];
        for (const key of this.cache.keys()) {
            if (key.includes(`${entityId}_${contentType}_${contentId}`)) {
                keysToDelete.push(key);
            }
        }
        keysToDelete.forEach(key => this.cache.delete(key));
        this.routeWaypointCache.clear();
    }

    /**
     * Clear all cache
     */
    clearCache() {
        this.cache.clear();
        this.routeWaypointCache.clear();
    }

    /**
     * Get cache statistics (for debugging)
     * @returns {Object} Cache statistics
     */
    getCacheStats() {
        const now = Date.now();
        let validEntries = 0;
        let expiredEntries = 0;

        for (const value of this.cache.values()) {
            if (now - value.timestamp < this.cacheTimeout) {
                validEntries++;
            } else {
                expiredEntries++;
            }
        }

        return {
            totalEntries: this.cache.size,
            validEntries,
            expiredEntries,
            cacheTimeout: this.cacheTimeout
        };
    }
}

export default new TranslationService();
